import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { History, Loader2, Phone, Video, Mail, MessageSquare, MapPin, Users, ClipboardCheck } from "lucide-react";
import AffiancamentoButton from "@/components/AffiancamentoButton";

const TYPE_LABEL = {
  affiancamento_agenzia: "Affiancamento agenzia", visita_partner: "Visita", visita: "Visita",
  telefonata: "Telefonata", videocall: "Video call", email_contatto: "Email / contatto",
  messaggio: "Messaggio", review_partner: "Review Partner",
};
const TYPE_ICON = {
  visita_partner: MapPin, visita: MapPin, telefonata: Phone, videocall: Video,
  email_contatto: Mail, messaggio: MessageSquare, affiancamento_agenzia: Users, review_partner: ClipboardCheck,
};
const ACTOR_LABEL = { agenzia: "Agenzia", responsabile_commerciale: "Resp. Commerciale" };
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function ActivityLog({ url, title = "Attività svolte sul cliente", testid = "activity-log", refreshKey, note = "Tutte le interazioni registrate: telefonate, visite, videocall, email e messaggi." }) {
  const [items, setItems] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setItems(null);
    api.get(url).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [url, refreshKey, tick]);

  return (
    <Card className="p-6" data-testid={testid}>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-lg font-bold tracking-tight">{title}</h2>
          {items && <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-muted-foreground" data-testid={`${testid}-count`}>{items.length}</span>}
        </div>
        {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
      </div>
      {items === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>Nessuna attività registrata.</p>
      ) : (
        <div className="max-h-[420px] space-y-2.5 overflow-y-auto pr-1">
          {items.map((it) => {
            const Icon = TYPE_ICON[it.type] || History;
            const done = it.status === "svolto";
            return (
              <div key={it.id} className="rounded-lg border border-border border-l-4 border-l-primary p-3" data-testid={`${testid}-item-${it.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">{TYPE_LABEL[it.type] || it.type}</span>
                    <Badge variant="outline" className={`text-[10px] ${done ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-amber-300 bg-amber-100 text-amber-800"}`}>
                      {done ? "Svolto" : "Pianificato"}
                    </Badge>
                    {it.decision_maker && <Badge className="bg-violet-600 text-[10px] text-white">Decision maker</Badge>}
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{fmtDate(it.date)}{it.time ? ` · ${it.time}` : ""}</span>
                </div>
                {it.notes && <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground/90">{it.notes}</p>}
                {it.outcome && <p className="mt-1 text-xs text-muted-foreground">Esito: {it.outcome}</p>}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span>{it.owner_name || ACTOR_LABEL[it.actor] || "—"}</span>
                  {it.prossima_azione && <span>Prossima azione: <b className="text-foreground">{it.prossima_azione}</b>{it.data_prossima_azione ? ` (${fmtDate(it.data_prossima_azione)})` : ""}</span>}
                </div>
                <div className="mt-2"><AffiancamentoButton visit={it} onDone={() => setTick((t) => t + 1)} /></div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
